// Prior-Period Restatement · Phase 1 — the "Prior-year adjustments (restatement)" sub-tab of Adjusting entries
// (components/restatement/*), EN + AR. Merged into lib/i18n.js translations (big-file rule: new strings in their own file).
export const I18N_RESTATEMENT = {
  en: {
    // PR 52 fixes 2: the restatement checks on the IFRS validation screen (statuses beyond PASS / FAIL)
    ifrs: { validation: { groups: { restatement: "Restatement" },
      status: { PASS: "PASS", FAIL: "FAIL", "N/A": "N/A", "NOT VERIFIED": "NOT VERIFIED", FLAG: "FLAG" } } },
    pya3b: {
      leadTitle: "Prior year restated — as previously reported and the restatement on each account",
      restatedPy: "Prior year (restated)",
      entries: "Restatement entries",
      ownAmount: "shown as its own amount (prior year typed / not available)",
      obTitle: "Restated prior-year closing balances",
      obRule: "The restated flag and its reason come from the posted restatement entries. The current-year opening of the client's journal is not changed; the current-year opening adjustment of the restatement is shown in its own column and the difference is taken after it.",
      cyOpeningAdj: "Current-year opening adjustment",
      difference: "Difference",
      restatedFlag: "Restated",
      restatedYes: "Restated",
      typedKept: "flag typed earlier kept, read-only",
      analyticsNote: "Prior year as previously reported: these analytics run on the client's own ledger; the restatement entries are not used here (see the statements for the restated comparatives).",
      scopingNote: "The prior year shown is as previously reported; restatement entries are not used for scoping.",
      listTitle: "Restatement of the comparative figures (posted entries)",
      profitEffect: "Effect on profit",
      ociEffect: "Effect on other comprehensive income",
      w9Title: "Prior-period misstatements (restated)",
      restatedByEntry: "restated by",
      farApr: "As previously reported",
    },
    pya: {
      tabCy: "Current-year adjusting entries",
      tab: "Prior-year adjustments (restatement)",
      title: "Prior-year adjustments (restatement)",
      subtitle: "Entries that restate the comparative (prior-year) figures. Draft, preview, then post; a posted entry is voided, never deleted.",
      warningTitle: "Before you use restatement entries",
      warning: "Restatement entries change the prior-year (restated) figures and the current-year opening balances. They update the statements, notes, lead sheets, ratios, opening balances and the report, and flag the affected sign-offs. Use them only for a prior-period error, a change in accounting policy or a reclassification of comparatives.",
      dontShow: "Don't show again for this engagement",
      understood: "I understand",
      newEntry: "New restatement entry",
      none: "No restatement entries.",
      locked: "The engagement is locked — restatement entries are read-only.",
      eqReadOnly: "You are the appointed engagement quality reviewer of this engagement — restatement entries are read-only for you.",
      reportFinal: "The auditor's report is Final — restatement entries are read-only.",
      comparative: "Comparative period: {start} to {end}",
      beforeComparative: "Before the comparative period",
      type: {
        prior_period_error: "Correction of a prior-period error",
        policy_change: "Change in accounting policy",
        reclassification: "Reclassification (re-presentation) only",
      },
      timing: { in_comparative: "In the comparative period", before_comparative: "Before the comparative period" },
      status: { draft: "Draft", posted: "Posted", voided: "Void" },
      col: { ref: "Ref", date: "Date", type: "Type", description: "Description", total: "Amount", status: "Status", link: "Linked to",
             by: "By / when", files: "Supporting documents" },
      voidedBy: "Void by {name} on {date}: {reason}",
      edit: "Edit", discard: "Discard", previewPost: "Preview and post", void: "Void", preview: "Preview",
      // Phase 2 — into the current year
      cy: {
        title: "Into the current year",
        hint: "How this restatement reaches the current year. The client's books (\"Unadjusted figures from client data\") never change.",
        treatment: {
          not_corrected: "Not corrected by the client — a current-year opening adjustment",
          corrected_in_cy_pl: "Corrected by the client in current-year profit or loss — move that correction to opening retained earnings",
          re_presentation: "Re-presentation only — no current-year entry",
        },
        reclassTitle: "Where the client booked the correction (current-year profit or loss)",
        reclassHint: "The total cannot be more than this entry's effect on equity.",
        account: "Account", amount: "Amount", addAccount: "Add account", pickPl: "Pick a profit-or-loss account", remove: "Remove",
        systemChip: "System entry from {ref} — read-only",
        pvTitle: "Current-year entry created on posting",
        pvNone: "No current-year entry (re-presentation only).",
        pvPlEffect: "Current-year P&L effect",
        pvOpening: "opening",
        pvLinked: "Posted with this entry: {ref}",
        problem: {
          RESTATEMENT_NO_RETAINED_EARNINGS_ACCOUNT: "No account of this engagement's chart is mapped to Retained earnings (2106) — map one before posting.",
          RESTATEMENT_CY_RECLASS_REQUIRED: "Pick the current-year profit-or-loss account(s) where the client booked the correction, and the amount.",
          RESTATEMENT_CY_RECLASS_EXCEEDS: "The reclassification is more than this entry's effect on equity.",
          RESTATEMENT_CY_RECLASS_ACCOUNT: "Pick profit-or-loss accounts only.",
          RESTATEMENT_CY_RECLASS_AMOUNT: "Enter amounts above 0.",
          RESTATEMENT_CY_RECLASS_NO_EQUITY_EFFECT: "This entry does not change equity — there is no current-year correction to move.",
          RESTATEMENT_CY_TREATMENT_INVALID: "This current-year treatment is not available for this type of entry.",
        },
      },
      dialog: {
        title: "Restatement entry", type: "Type", timing: "When", date: "Date (in the comparative period)", description: "Description",
        reason: "Reason", reasonHint: "Why the comparatives are restated (for a policy change: why the new policy gives more relevant information).",
        misstatement: "Link to a misstatement", noMisstatement: "— none —", finding: "Link to a finding (reference)",
        account: "Account", debit: "Debit", credit: "Credit", narration: "Narration", difference: "Difference", balanced: "balanced",
        addLine: "Add line", removeLine: "Remove line", pickAccount: "Pick an account", saveDraft: "Save draft",
        beforeHint: "Adjusts the comparative period's opening balances — use balance-sheet and equity (retained earnings) accounts.",
        postedReadOnly: "This entry is posted — it is never edited. Void it (with a reason) and make a new one.",
        voidedReadOnly: "This entry is void — kept for the record, read-only.",
        saved: "Draft saved",
      },
      pv: {
        title: "Preview — {ref}",
        intro: "What posting this entry does (with every other posted restatement entry). Nothing is saved until you post.",
        line: "Line", pyApr: "Prior year as previously reported", pyRestated: "Prior year restated", thisEntry: "This entry",
        cyOpening: "Current-year opening (recorded)", cyOpeningRestated: "Current-year opening restated",
        pyBsNotAvailable: "The prior-year balance sheet is not available (the prior-year data carries no opening balances) — only the effect is shown.",
        thirdTitle: "Third balance sheet (start of the comparative period)",
        thirdRequired: "Required — the effect on the comparative period's opening balances ({amount}) is material against overall materiality ({om}).",
        thirdNotRequired: "Not required — largest effect on the comparative period's opening balances {amount} (overall materiality {om}).",
        flagsTitle: "Sign-offs that will be flagged \"changed after sign-off\"",
        flagsNote: "Exactly these are flagged when the entry is posted (and again if it is voided).",
        item: {
          statements: "Financial statements — published copy to regenerate", note: "Note {key}", lead_sheet: "Lead sheet {key}",
          a9003: "Opening balances (A9003)", w9: "Misstatement worksheet (W9)", w10: "Completion memorandum (W10)",
          report: "Auditor's report draft (signed by the Partner)", form: "Final analytical review (316(a))",
          form_review: "Final analytical review (R316(a))", adjusting_entries: "Adjusting entries (AJE)",
        },
        confirm: "I have reviewed the effect above",
        post: "Post entry",
        posted: "Restatement entry posted",
      },
      voidTitle: "Void {ref}", voidReason: "Reason for voiding (required)", voidConfirm: "Void entry", voided: "Entry voided",
    },
    errors: {
      RESTATEMENT_TYPE_INVALID: "Type must be correction of a prior-period error, change in accounting policy or reclassification.",
      RESTATEMENT_DATE_OUT_OF_PERIOD: "The entry date must lie in the comparative period — or choose \"before the comparative period\".",
      RESTATEMENT_BEFORE_PERIOD_PL_LINE: "An entry before the comparative period adjusts opening balances — use balance-sheet and equity accounts.",
      RESTATEMENT_UNBALANCED: "The entry must balance (debits = credits).",
      RESTATEMENT_CONFIRM_REQUIRED: "Open the preview and confirm the effect before posting.",
      RESTATEMENT_REASON_REQUIRED: "Enter the reason for the restatement before posting.",
      RESTATEMENT_DESCRIPTION_REQUIRED: "Enter a description before posting.",
      RESTATEMENT_REPORT_FINAL: "The auditor's report is Final — restatement entries can no longer be changed.",
      RESTATEMENT_NOT_DRAFT: "Only a draft can be changed; void a posted entry.",
      RESTATEMENT_VOID_REASON: "Give the reason for voiding the entry.",
      RESTATEMENT_EQ_REVIEWER_READ_ONLY: "You are the appointed engagement quality reviewer of this engagement — restatement entries are read-only for you.",
      AJE_IS_RESTATEMENT: "This is a prior-year restatement entry — open it on its own tab.",
    },
    // Phase 3a — the restated statements on the IFRS screens
    pyaFs: {
      restated: "Restated",
      banner: "The comparative figures are restated — see note {n}.",
      bannerLink: "Prior-year adjustments",
      thirdTitle: "Third statement of financial position — at {date}",
      thirdRequired: "Presented: the restatement's effect on the opening balances of the comparative period ({eff}) reaches overall materiality ({om}).",
      thirdNotRequired: "Not presented: the restatement's effect on the opening balances of the comparative period ({eff}) is below overall materiality ({om}).",
      thirdOverride: "Override by {by} on {at}: {reason}",
      thirdOverrideTitle: "Override (Partner / Manager)",
      thirdPresent: "Present it",
      thirdOmit: "Do not present it",
      thirdClear: "Clear the override",
      thirdReason: "Reason for the override",
      thirdReasonRequired: "Give the reason for the override.",
      thirdSaved: "Third balance sheet override saved.",
      totalAssets: "Total assets",
      totalEqLiab: "Total equity and liabilities",
      asPreviouslyReported: "As previously reported",
      effect: "Restatement",
      openingEffect: "Effect on the opening balances",
      line: "Line",
      noteTitle: "Restatement of comparative figures",
      checkAfterRestatement: "Check the manual prior-year amounts after the restatement.",
      RESTATEMENT_THIRD_BS_ROLE: "Only a Partner or a Manager may override the third balance sheet.",
      RESTATEMENT_THIRD_BS_REASON: "Give the reason for the override.",
    },
  },
  ar: {
    ifrs: { validation: { groups: { restatement: "إعادة العرض" },
      status: { PASS: "ناجح", FAIL: "فاشل", "N/A": "لا ينطبق", "NOT VERIFIED": "لم يتم التحقق", FLAG: "للمراجعة" } } },
    pya3b: {
      leadTitle: "أُعيد عرض السنة السابقة — كما سبق عرضها ومبلغ إعادة العرض لكل حساب",
      restatedPy: "السنة السابقة (بعد إعادة العرض)",
      entries: "قيود إعادة العرض",
      ownAmount: "يظهر كمبلغ مستقل (السنة السابقة مُدخلة يدوياً / غير متاحة)",
      obTitle: "أرصدة الإقفال للسنة السابقة بعد إعادة العرض",
      obRule: "علامة إعادة العرض وسببها مأخوذان من قيود إعادة العرض المرحّلة. لا يتغير رصيد الافتتاح للسنة الحالية في قيد العميل؛ يظهر قيد تسوية الافتتاح للسنة الحالية الخاص بإعادة العرض في عموده، ويُحسب الفرق بعده.",
      cyOpeningAdj: "تسوية الافتتاح للسنة الحالية",
      difference: "الفرق",
      restatedFlag: "أُعيد عرضه",
      restatedYes: "أُعيد عرضه",
      typedKept: "العلامة المُدخلة سابقاً محفوظة للعرض فقط",
      analyticsNote: "السنة السابقة كما سبق عرضها: تعمل هذه التحليلات على دفتر أستاذ العميل؛ لا تُستخدم قيود إعادة العرض هنا (انظر القوائم لأرقام المقارنة بعد إعادة العرض).",
      scopingNote: "السنة السابقة المعروضة كما سبق عرضها؛ لا تُستخدم قيود إعادة العرض في تحديد النطاق.",
      listTitle: "إعادة عرض أرقام المقارنة (القيود المرحّلة)",
      profitEffect: "الأثر على الربح",
      ociEffect: "الأثر على الدخل الشامل الآخر",
      w9Title: "تحريفات فترات سابقة (أُعيد عرضها)",
      restatedByEntry: "أُعيد عرضه بالقيد",
      farApr: "كما سبق عرضها",
    },
    pya: {
      tabCy: "قيود تسوية السنة الحالية",
      tab: "تسويات السنة السابقة (إعادة العرض)",
      title: "تسويات السنة السابقة (إعادة العرض)",
      subtitle: "قيود تعيد عرض أرقام المقارنة (السنة السابقة). أنشئ مسودة ثم عاين ثم رحّل؛ القيد المرحّل يُلغى ولا يُحذف.",
      warningTitle: "قبل استخدام قيود إعادة العرض",
      warning: "تغيّر قيود إعادة العرض أرقام السنة السابقة (المعاد عرضها) والأرصدة الافتتاحية للسنة الحالية. وهي تحدّث القوائم والإيضاحات وأوراق التجميع والنسب والأرصدة الافتتاحية والتقرير، وتؤشر على التوقيعات المتأثرة. استخدمها فقط لتصحيح خطأ فترة سابقة أو لتغيير في السياسة المحاسبية أو لإعادة تبويب أرقام المقارنة.",
      dontShow: "لا تُظهر هذا مرة أخرى لهذا الارتباط",
      understood: "فهمت",
      newEntry: "قيد إعادة عرض جديد",
      none: "لا توجد قيود إعادة عرض.",
      locked: "الارتباط مقفل — قيود إعادة العرض للقراءة فقط.",
      eqReadOnly: "أنت مراجع جودة الارتباط المعيَّن لهذا الارتباط — قيود إعادة العرض للقراءة فقط بالنسبة لك.",
      reportFinal: "تقرير المراجع نهائي — قيود إعادة العرض للقراءة فقط.",
      comparative: "فترة المقارنة: من {start} إلى {end}",
      beforeComparative: "قبل فترة المقارنة",
      type: {
        prior_period_error: "تصحيح خطأ فترة سابقة",
        policy_change: "تغيير في السياسة المحاسبية",
        reclassification: "إعادة تبويب (إعادة عرض) فقط",
      },
      timing: { in_comparative: "ضمن فترة المقارنة", before_comparative: "قبل فترة المقارنة" },
      status: { draft: "مسودة", posted: "مرحّل", voided: "ملغى" },
      col: { ref: "المرجع", date: "التاريخ", type: "النوع", description: "الوصف", total: "المبلغ", status: "الحالة", link: "مرتبط بـ",
             by: "بواسطة / متى", files: "المستندات المؤيدة" },
      voidedBy: "ألغاه {name} في {date}: {reason}",
      edit: "تعديل", discard: "حذف المسودة", previewPost: "معاينة وترحيل", void: "إلغاء", preview: "معاينة",
      // المرحلة 2 — الأثر على السنة الحالية
      cy: {
        title: "الأثر على السنة الحالية",
        hint: "كيف يصل أثر إعادة العرض إلى السنة الحالية. دفاتر العميل («أرقام غير معدلة من بيانات العميل») لا تتغير أبدًا.",
        treatment: {
          not_corrected: "لم يصححه العميل — تسوية للأرصدة الافتتاحية للسنة الحالية",
          corrected_in_cy_pl: "صححه العميل في ربح أو خسارة السنة الحالية — نقل ذلك التصحيح إلى الأرباح المبقاة الافتتاحية",
          re_presentation: "إعادة عرض فقط — لا يوجد قيد في السنة الحالية",
        },
        reclassTitle: "أين سجّل العميل التصحيح (ربح أو خسارة السنة الحالية)",
        reclassHint: "لا يجوز أن يزيد المجموع على أثر هذا القيد على حقوق الملكية.",
        account: "الحساب", amount: "المبلغ", addAccount: "إضافة حساب", pickPl: "اختر حساب ربح أو خسارة", remove: "إزالة",
        systemChip: "قيد نظام من {ref} — للقراءة فقط",
        pvTitle: "قيد السنة الحالية الذي يُنشأ عند الترحيل",
        pvNone: "لا يوجد قيد في السنة الحالية (إعادة عرض فقط).",
        pvPlEffect: "الأثر على ربح أو خسارة السنة الحالية",
        pvOpening: "افتتاحي",
        pvLinked: "رُحّل مع هذا القيد: {ref}",
        problem: {
          RESTATEMENT_NO_RETAINED_EARNINGS_ACCOUNT: "لا يوجد حساب في دليل حسابات هذا الارتباط مربوط بالأرباح المبقاة (2106) — اربط حسابًا قبل الترحيل.",
          RESTATEMENT_CY_RECLASS_REQUIRED: "اختر حساب (حسابات) الربح أو الخسارة في السنة الحالية الذي سجّل فيه العميل التصحيح، والمبلغ.",
          RESTATEMENT_CY_RECLASS_EXCEEDS: "إعادة التبويب أكبر من أثر هذا القيد على حقوق الملكية.",
          RESTATEMENT_CY_RECLASS_ACCOUNT: "اختر حسابات الربح أو الخسارة فقط.",
          RESTATEMENT_CY_RECLASS_AMOUNT: "أدخل مبالغ أكبر من صفر.",
          RESTATEMENT_CY_RECLASS_NO_EQUITY_EFFECT: "هذا القيد لا يغيّر حقوق الملكية — لا يوجد تصحيح في السنة الحالية لنقله.",
          RESTATEMENT_CY_TREATMENT_INVALID: "هذه المعالجة في السنة الحالية غير متاحة لهذا النوع من القيود.",
        },
      },
      dialog: {
        title: "قيد إعادة عرض", type: "النوع", timing: "التوقيت", date: "التاريخ (ضمن فترة المقارنة)", description: "الوصف",
        reason: "السبب", reasonHint: "لماذا يُعاد عرض أرقام المقارنة (لتغيير السياسة: لماذا تعطي السياسة الجديدة معلومات أكثر ملاءمة).",
        misstatement: "ربط بتحريف", noMisstatement: "— لا يوجد —", finding: "ربط بملاحظة (مرجع)",
        account: "الحساب", debit: "مدين", credit: "دائن", narration: "البيان", difference: "الفرق", balanced: "متوازن",
        addLine: "إضافة سطر", removeLine: "حذف السطر", pickAccount: "اختر حسابًا", saveDraft: "حفظ المسودة",
        beforeHint: "يعدّل الأرصدة الافتتاحية لفترة المقارنة — استخدم حسابات قائمة المركز المالي وحقوق الملكية (الأرباح المبقاة).",
        postedReadOnly: "هذا القيد مرحّل ولا يُعدَّل. ألغِه (مع ذكر السبب) وأنشئ قيدًا جديدًا.",
        voidedReadOnly: "هذا القيد ملغى — محفوظ للسجل وللقراءة فقط.",
        saved: "حُفظت المسودة",
      },
      pv: {
        title: "معاينة — {ref}",
        intro: "ما يفعله ترحيل هذا القيد (مع كل قيود إعادة العرض المرحّلة الأخرى). لا يُحفظ شيء حتى الترحيل.",
        line: "البند", pyApr: "السنة السابقة كما سبق عرضها", pyRestated: "السنة السابقة المعاد عرضها", thisEntry: "هذا القيد",
        cyOpening: "افتتاحي السنة الحالية (المسجّل)", cyOpeningRestated: "افتتاحي السنة الحالية المعاد عرضه",
        pyBsNotAvailable: "قائمة المركز المالي للسنة السابقة غير متاحة (بيانات السنة السابقة بلا أرصدة افتتاحية) — يُعرض الأثر فقط.",
        thirdTitle: "قائمة مركز مالي ثالثة (بداية فترة المقارنة)",
        thirdRequired: "مطلوبة — الأثر على الأرصدة الافتتاحية لفترة المقارنة ({amount}) جوهري مقارنة بالأهمية النسبية الإجمالية ({om}).",
        thirdNotRequired: "غير مطلوبة — أكبر أثر على الأرصدة الافتتاحية لفترة المقارنة {amount} (الأهمية النسبية الإجمالية {om}).",
        flagsTitle: "التوقيعات التي سيُؤشَّر عليها «تغيّر بعد التوقيع»",
        flagsNote: "يُؤشَّر على هذه البنود نفسها عند ترحيل القيد (ومرة أخرى إذا أُلغي).",
        item: {
          statements: "القوائم المالية — يُعاد إنشاء النسخة المنشورة", note: "الإيضاح {key}", lead_sheet: "ورقة التجميع {key}",
          a9003: "الأرصدة الافتتاحية (A9003)", w9: "ورقة التحريفات (W9)", w10: "مذكرة الإنجاز (W10)",
          report: "مسودة تقرير المراجع (موقّعة من الشريك)", form: "المراجعة التحليلية النهائية (316(a))",
          form_review: "المراجعة التحليلية النهائية (R316(a))", adjusting_entries: "قيود التسوية (AJE)",
        },
        confirm: "راجعتُ الأثر أعلاه",
        post: "ترحيل القيد",
        posted: "رُحِّل قيد إعادة العرض",
      },
      voidTitle: "إلغاء {ref}", voidReason: "سبب الإلغاء (مطلوب)", voidConfirm: "إلغاء القيد", voided: "أُلغي القيد",
    },
    errors: {
      RESTATEMENT_TYPE_INVALID: "يجب أن يكون النوع تصحيح خطأ فترة سابقة أو تغيير سياسة محاسبية أو إعادة تبويب.",
      RESTATEMENT_DATE_OUT_OF_PERIOD: "يجب أن يقع تاريخ القيد ضمن فترة المقارنة — أو اختر «قبل فترة المقارنة».",
      RESTATEMENT_BEFORE_PERIOD_PL_LINE: "القيد السابق لفترة المقارنة يعدّل الأرصدة الافتتاحية — استخدم حسابات قائمة المركز المالي وحقوق الملكية.",
      RESTATEMENT_UNBALANCED: "يجب أن يتوازن القيد (المدين = الدائن).",
      RESTATEMENT_CONFIRM_REQUIRED: "افتح المعاينة وأكّد الأثر قبل الترحيل.",
      RESTATEMENT_REASON_REQUIRED: "أدخل سبب إعادة العرض قبل الترحيل.",
      RESTATEMENT_DESCRIPTION_REQUIRED: "أدخل وصفًا قبل الترحيل.",
      RESTATEMENT_REPORT_FINAL: "تقرير المراجع نهائي — لم يعد بالإمكان تغيير قيود إعادة العرض.",
      RESTATEMENT_NOT_DRAFT: "تُعدَّل المسودة فقط؛ ألغِ القيد المرحّل.",
      RESTATEMENT_VOID_REASON: "اذكر سبب إلغاء القيد.",
      RESTATEMENT_EQ_REVIEWER_READ_ONLY: "أنت مراجع جودة الارتباط المعيَّن لهذا الارتباط — قيود إعادة العرض للقراءة فقط بالنسبة لك.",
      AJE_IS_RESTATEMENT: "هذا قيد إعادة عرض للسنة السابقة — افتحه من تبويبه الخاص.",
    },
    pyaFs: {
      restated: "مُعاد عرضه",
      banner: "أُعيد عرض أرقام المقارنة — انظر الإيضاح {n}.",
      bannerLink: "تسويات السنة السابقة",
      thirdTitle: "قائمة المركز المالي الثالثة — في {date}",
      thirdRequired: "تُعرض: أثر إعادة العرض على الأرصدة الافتتاحية لفترة المقارنة ({eff}) يبلغ الأهمية النسبية الإجمالية ({om}).",
      thirdNotRequired: "لا تُعرض: أثر إعادة العرض على الأرصدة الافتتاحية لفترة المقارنة ({eff}) أقل من الأهمية النسبية الإجمالية ({om}).",
      thirdOverride: "تجاوز بواسطة {by} في {at}: {reason}",
      thirdOverrideTitle: "التجاوز (الشريك / المدير)",
      thirdPresent: "اعرضها",
      thirdOmit: "لا تعرضها",
      thirdClear: "ألغِ التجاوز",
      thirdReason: "سبب التجاوز",
      thirdReasonRequired: "اذكر سبب التجاوز.",
      thirdSaved: "حُفظ تجاوز قائمة المركز المالي الثالثة.",
      totalAssets: "إجمالي الموجودات",
      totalEqLiab: "إجمالي حقوق الملكية والمطلوبات",
      asPreviouslyReported: "كما سبق عرضه",
      effect: "إعادة العرض",
      openingEffect: "الأثر على الأرصدة الافتتاحية",
      line: "البند",
      noteTitle: "إعادة عرض أرقام المقارنة",
      checkAfterRestatement: "راجع مبالغ السنة السابقة المُدخلة يدويًا بعد إعادة العرض.",
      RESTATEMENT_THIRD_BS_ROLE: "لا يتجاوز قرار قائمة المركز المالي الثالثة إلا الشريك أو المدير.",
      RESTATEMENT_THIRD_BS_REASON: "اذكر سبب التجاوز.",
    },
  },
};

// PR 52 fixes (owner review 21-Oct-2026), part A — B1 / B2 / B3 / N-3 strings, merged into the blocks above (kept apart so the
// two parts of the card do not edit the same lines)
const PR52A = {
  en: {
    pya: {
      timing: { mixed: "Both — some lines before the comparative period" },
      dialog: {
        lineBefore: "Before the comparative period",
        mixedHint: "Tick the lines that adjust the opening balances (before the comparative period); the other lines are dated in the comparative period. Each group must balance on its own.",
      },
      cy: {
        treatment: { recorded_by_client: "Already recorded by the client in the current year's opening balances / retained earnings — no current-year entry" },
        rePresentationHint: "For a correction of an error or a change in accounting policy: only when the entry leaves the prior-year closing balances unchanged.",
        clientTitle: "The client's current-year entry",
        clientHint: "Enter the client's voucher number (current-year ledger), or its accounts and amounts. It must equal this entry's effect on the prior-year closing balances, account by account.",
        clientVoucher: "Voucher number",
        clientOrLines: "No voucher? Enter its balance-sheet accounts and amounts:",
        pvClient: "The client's entry {voucher} is read as the restatement of the current-year opening balances — no current-year entry is created.",
        pvDiffAccount: "Account", pvDiffClient: "Client's entry", pvDiffRestatement: "This restatement", pvDiffDifference: "Difference",
        problem: {
          RESTATEMENT_CY_CLIENT_MISMATCH: "The client's entry does not equal this restatement's effect on the prior-year closing balances — see the difference per account.",
          RESTATEMENT_CY_CLIENT_ALREADY_RECORDED: "The client appears to have recorded this correction in the current year — use treatment (d) \"already recorded by the client\".",
          RESTATEMENT_CY_CLIENT_NOT_IN_LEDGER: "The entered lines are not in the client's current-year ledger — pick the client's voucher.",
          RESTATEMENT_CY_CLIENT_REQUIRED: "Enter the client's voucher number, or its accounts and amounts.",
          RESTATEMENT_CY_CLIENT_VOUCHER_UNKNOWN: "That voucher is not in the client's current-year ledger.",
          RESTATEMENT_CY_CLIENT_PL_LINE: "The client's entry has profit-or-loss lines — use \"corrected by the client in current-year profit or loss\".",
          RESTATEMENT_NO_RESERVE_ACCOUNT: "An other-comprehensive-income line closes into a reserve no account of this chart is mapped to — map the reserve account first.",
        },
      },
      pv: {
        thirdNotAvailable: "Not available (no prior-year ledger) — the prior year has no opening balances (a trial balance), so no balance sheet at the start of the comparative period can be built.",
        thirdCannotDetermine: "Cannot determine — set overall materiality (largest effect on the comparative period's opening balances {amount}).",
      },
    },
    pyaFs: {
      thirdNotAvailable: "Not available (no prior-year ledger): the prior year has no opening balances, so no statement of financial position at the start of the comparative period can be built.",
      thirdCannotDetermine: "Cannot determine — set overall materiality (effect on the opening balances of the comparative period: {eff}).",
    },
  },
  ar: {
    pya: {
      timing: { mixed: "كلاهما — بعض الأسطر قبل فترة المقارنة" },
      dialog: {
        lineBefore: "قبل فترة المقارنة",
        mixedHint: "حدّد الأسطر التي تعدّل الأرصدة الافتتاحية (قبل فترة المقارنة)؛ أما الأسطر الأخرى فتاريخها ضمن فترة المقارنة. يجب أن تتوازن كل مجموعة وحدها.",
      },
      cy: {
        treatment: { recorded_by_client: "سجّله العميل مسبقًا في الأرصدة الافتتاحية / الأرباح المبقاة للسنة الحالية — لا يوجد قيد في السنة الحالية" },
        rePresentationHint: "لتصحيح خطأ أو تغيير سياسة محاسبية: فقط إذا كان القيد لا يغيّر الأرصدة الختامية للسنة السابقة.",
        clientTitle: "قيد العميل في السنة الحالية",
        clientHint: "أدخل رقم قيد العميل (دفتر أستاذ السنة الحالية)، أو حساباته ومبالغه. يجب أن يساوي أثر هذا القيد على الأرصدة الختامية للسنة السابقة، حسابًا بحساب.",
        clientVoucher: "رقم القيد",
        clientOrLines: "لا يوجد رقم قيد؟ أدخل حسابات قائمة المركز المالي ومبالغها:",
        pvClient: "يُقرأ قيد العميل {voucher} على أنه إعادة عرض الأرصدة الافتتاحية للسنة الحالية — لا يُنشأ قيد في السنة الحالية.",
        pvDiffAccount: "الحساب", pvDiffClient: "قيد العميل", pvDiffRestatement: "إعادة العرض هذه", pvDiffDifference: "الفرق",
        problem: {
          RESTATEMENT_CY_CLIENT_MISMATCH: "قيد العميل لا يساوي أثر إعادة العرض هذه على الأرصدة الختامية للسنة السابقة — انظر الفرق لكل حساب.",
          RESTATEMENT_CY_CLIENT_ALREADY_RECORDED: "يبدو أن العميل سجّل هذا التصحيح في السنة الحالية — استخدم المعالجة (د) «سجّله العميل مسبقًا».",
          RESTATEMENT_CY_CLIENT_NOT_IN_LEDGER: "الأسطر المُدخلة غير موجودة في دفتر الأستاذ للسنة الحالية لدى العميل — اختر قيد العميل.",
          RESTATEMENT_CY_CLIENT_REQUIRED: "أدخل رقم قيد العميل، أو حساباته ومبالغه.",
          RESTATEMENT_CY_CLIENT_VOUCHER_UNKNOWN: "هذا القيد غير موجود في دفتر الأستاذ للسنة الحالية لدى العميل.",
          RESTATEMENT_CY_CLIENT_PL_LINE: "قيد العميل يحتوي على أسطر ربح أو خسارة — استخدم «صححه العميل في ربح أو خسارة السنة الحالية».",
          RESTATEMENT_NO_RESERVE_ACCOUNT: "أحد أسطر الدخل الشامل الآخر يُقفل في احتياطي لا يوجد له حساب في هذا الدليل — اربط حساب الاحتياطي أولًا.",
        },
      },
      pv: {
        thirdNotAvailable: "غير متاحة (لا يوجد دفتر أستاذ للسنة السابقة) — لا توجد أرصدة افتتاحية للسنة السابقة (ميزان مراجعة)، فلا يمكن إعداد قائمة مركز مالي في بداية فترة المقارنة.",
        thirdCannotDetermine: "تعذّر التحديد — حدّد الأهمية النسبية الإجمالية (أكبر أثر على الأرصدة الافتتاحية لفترة المقارنة {amount}).",
      },
    },
    pyaFs: {
      thirdNotAvailable: "غير متاحة (لا يوجد دفتر أستاذ للسنة السابقة): لا توجد أرصدة افتتاحية للسنة السابقة، فلا يمكن إعداد قائمة المركز المالي في بداية فترة المقارنة.",
      thirdCannotDetermine: "تعذّر التحديد — حدّد الأهمية النسبية الإجمالية (الأثر على الأرصدة الافتتاحية لفترة المقارنة: {eff}).",
    },
  },
};
const _mergePr52a = (dst, src) => {
  Object.entries(src).forEach(([k, v]) => {
    if (v && typeof v === "object" && !Array.isArray(v)) { dst[k] = dst[k] || {}; _mergePr52a(dst[k], v); } else dst[k] = v;
  });
};
_mergePr52a(I18N_RESTATEMENT, PR52A);

export default I18N_RESTATEMENT;
